/**
 * Dialog di inserimento/modifica del bottone CTA nel corpo newsletter.
 * Raccoglie testo, link e colore; l'anteprima mostra la resa reale nell'email.
 */
import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Box, Dialog, DialogActions, DialogContent, DialogTitle, Stack, Typography } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
//--------------------------------------------------------
import { DefaultContainedButton, DefaultOutlinedButton, RedOutlinedButton } from '../../../../../utils/Buttons';
import { CustomTextField } from '../../../../../utils/StyledInputs';
import { BUTTON_COLOR_PRESETS, DEFAULT_BUTTON_COLOR, readableTextColor } from './EmailButton';

const EMPTY = { label: 'Scopri di più', href: '', bgColor: DEFAULT_BUTTON_COLOR };

/**
 * @param {{
 *   open: boolean,
 *   initialValues?: { label?: string, href?: string, bgColor?: string } | null,
 *   isEdit?: boolean,
 *   onClose: () => void,
 *   onConfirm: (values: { label: string, href: string, bgColor: string }) => void,
 *   onRemove?: () => void,
 * }} props
 */
export function ButtonDialog({ open, initialValues, isEdit = false, onClose, onConfirm, onRemove }) {
  const [values, setValues] = useState(EMPTY);
  const [touched, setTouched] = useState(false);

  // Ripopola i campi ad ogni apertura (inserimento = valori di default, modifica = valori del nodo).
  useEffect(() => {
    if (open) {
      setValues({ ...EMPTY, ...(initialValues ?? {}) });
      setTouched(false);
    }
  }, [open, initialValues]);

  const label = values.label.trim();
  const href = values.href.trim();
  const labelError = touched && label === '';
  const hrefError = touched && href === '';

  const handleConfirm = () => {
    setTouched(true);
    if (label === '' || href === '') return;
    onConfirm({ label, href, bgColor: values.bgColor });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{isEdit ? 'Modifica bottone' : 'Inserisci bottone'}</DialogTitle>

      <DialogContent>
        <Stack spacing={2.5} sx={{ pt: 1 }}>
          <CustomTextField
            label="Testo del bottone"
            value={values.label}
            onChange={(e) => setValues((v) => ({ ...v, label: e.target.value }))}
            error={labelError}
            helperText={labelError ? 'Il testo è obbligatorio' : ' '}
            fullWidth
          />

          <CustomTextField
            label="Link"
            placeholder="https://esempio.it/pagina"
            value={values.href}
            onChange={(e) => setValues((v) => ({ ...v, href: e.target.value }))}
            error={hrefError}
            helperText={hrefError ? 'Il link è obbligatorio' : 'Se ometti https:// viene aggiunto automaticamente'}
            fullWidth
          />

          <Box>
            <Typography variant="body2" sx={{ mb: 1, color: '#637381' }}>
              Colore del bottone
            </Typography>
            <Stack direction="row" flexWrap="wrap" alignItems="center" gap={1}>
              {BUTTON_COLOR_PRESETS.map((color) => (
                <Box
                  key={color}
                  role="button"
                  aria-label={`Colore ${color}`}
                  onClick={() => setValues((v) => ({ ...v, bgColor: color }))}
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: '50%',
                    backgroundColor: color,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: values.bgColor === color ? '2px solid #212b36' : '2px solid transparent',
                  }}
                >
                  {values.bgColor === color && (
                    <CheckIcon sx={{ fontSize: 18, color: readableTextColor(color) }} />
                  )}
                </Box>
              ))}

              {/* Colore libero: selettore nativo, per uscire dalla palette proposta. */}
              <Box
                component="input"
                type="color"
                aria-label="Colore personalizzato"
                value={values.bgColor}
                onChange={(e) => setValues((v) => ({ ...v, bgColor: e.target.value }))}
                sx={{
                  width: 32,
                  height: 32,
                  padding: 0,
                  border: '1px solid #c4c4c4',
                  borderRadius: '50%',
                  background: 'none',
                  cursor: 'pointer',
                }}
              />
            </Stack>
          </Box>

          <Box>
            <Typography variant="body2" sx={{ mb: 1, color: '#637381' }}>
              Anteprima
            </Typography>
            <Box sx={{ p: 2, backgroundColor: '#f4f6f8', borderRadius: 1 }}>
              <Box
                component="span"
                sx={{
                  display: 'inline-block',
                  backgroundColor: values.bgColor,
                  color: readableTextColor(values.bgColor),
                  padding: '12px 24px',
                  borderRadius: '6px',
                  fontSize: '15px',
                  fontWeight: 'bold',
                  lineHeight: 1.2,
                  overflowWrap: 'anywhere',
                }}
              >
                {label || 'Testo del bottone'}
              </Box>
            </Box>
          </Box>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2, justifyContent: isEdit ? 'space-between' : 'flex-end' }}>
        {isEdit && <RedOutlinedButton onClick={onRemove}>Rimuovi</RedOutlinedButton>}
        <Stack direction="row" spacing={1}>
          <DefaultOutlinedButton onClick={onClose}>Annulla</DefaultOutlinedButton>
          <DefaultContainedButton onClick={handleConfirm}>{isEdit ? 'Salva' : 'Inserisci'}</DefaultContainedButton>
        </Stack>
      </DialogActions>
    </Dialog>
  );
}

ButtonDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  initialValues: PropTypes.object,
  isEdit: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  onRemove: PropTypes.func,
};

export default ButtonDialog;
